/**
 * FeedbackDialog: "Send feedback" from the header. Type picker (keyboard reachable menu) + message.
 */
import { useId, useState } from "react";
import { Icon } from "@iconify/react";
import Dialog from "./Dialog";
import DropdownMenu from "./DropdownMenu";
import Button from "./Button";
import { TextArea } from "./TextField";
import { useToast } from "@/context/ToastContext";
import { submitFeedback } from "@/services/feedback.service";

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const feedbackTypes = [
  { value: "bug", label: "Bug report", icon: "mdi:bug" },
  { value: "feature", label: "Feature request", icon: "mdi:lightbulb-outline" },
  { value: "improvement", label: "Improvement", icon: "mdi:trending-up" },
  { value: "question", label: "Question", icon: "mdi:help-circle-outline" },
  { value: "other", label: "Other", icon: "mdi:chat-outline" },
] as const;

type FeedbackType = (typeof feedbackTypes)[number]["value"];

export default function FeedbackDialog({ isOpen, onClose }: FeedbackDialogProps) {
  const [feedbackType, setFeedbackType] = useState<FeedbackType | "">("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [typeError, setTypeError] = useState(false);
  const { addToast } = useToast();
  const typeLabelId = useId();
  const typeValueId = useId();

  const selectedType = feedbackTypes.find(type => type.value === feedbackType);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackType) {
      setTypeError(true);
      return;
    }
    if (!message.trim()) return;

    setIsSubmitting(true);
    try {
      const result = await submitFeedback({ type: feedbackType, message: message.trim() });
      if (result.success) {
        addToast({ message: "Thanks for the feedback. We read every message.", type: "success" });
        setFeedbackType("");
        setMessage("");
        onClose();
      } else {
        addToast({ message: result.error || "Couldn't send your feedback. Try again.", type: "error" });
      }
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      addToast({ message: "Couldn't send your feedback. Try again.", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setFeedbackType("");
    setMessage("");
    setTypeError(false);
    onClose();
  };

  const dropdownItems = feedbackTypes.map(type => ({
    label: type.label,
    icon: type.icon,
    onClick: () => {
      setFeedbackType(type.value);
      setTypeError(false);
    },
  }));

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} title="Send feedback" width="md" dismissible={!isSubmitting}>
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <div>
          <p id={typeLabelId} className="mb-1.5 text-sm font-medium text-fg-secondary">
            What kind of feedback is it?
          </p>
          <DropdownMenu
            trigger={
              <span
                className={`flex h-10 w-full items-center justify-between gap-2 rounded-control border bg-surface px-3 transition-colors hover:border-fg-muted/60 dark:bg-white/5 ${
                  typeError ? "border-danger" : "border-line-strong"
                }`}
              >
                {selectedType ? (
                  <span id={typeValueId} className="flex items-center gap-2">
                    <Icon icon={selectedType.icon} className="text-base text-fg-muted" aria-hidden="true" />
                    <span className="text-sm text-fg">{selectedType.label}</span>
                  </span>
                ) : (
                  <span id={typeValueId} className="text-sm text-fg-muted">Choose a type</span>
                )}
                <Icon icon="mdi:chevron-down" className="text-lg text-fg-muted" aria-hidden="true" />
              </span>
            }
            triggerProps={{ "aria-labelledby": `${typeLabelId} ${typeValueId}` }}
            items={dropdownItems}
            position="bottom-left"
            matchTriggerWidth
            className="max-h-60"
          />
          {typeError && (
            <p className="mt-1.5 text-13 text-danger-text" role="alert">
              Choose what kind of feedback this is.
            </p>
          )}
        </div>

        <TextArea
          label="Your message"
          name="message"
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="What happened, or what would you like to see?"
          rows={4}
          disabled={isSubmitting}
          maxLength={500}
          showCount
          required
        />

        <div className="flex gap-2 pt-1">
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting} fullWidth>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} loadingText="Sending..." disabled={!message.trim()} fullWidth>
            Send feedback
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
